import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { useNavigate, useParams } from 'react-router-dom';
import { discountsPathTo, productsPathTo } from '../utils/Api';
import SnackbarContext from '../contexts/SnackbarContext';
import FormBox from './building_blocks/FormBox';
import PageHeader from './building_blocks/PageHeader';
import CurrencyField from './CurrencyField';
import { currency } from './building_blocks/Currency';
import moment from 'moment';

const blankDiscount = {
  name: '',
  starts_at: null,
  ends_at: null,
  discount_items: [],
};

const toMomentDateTime = (value) => {
  const date = moment(value);
  return date.isValid() ? date : null;
};

const toApiDateTime = (value) => (value && value.isValid() ? value.toISOString() : null);

const ProviderDiscountForm = () => {
  const { id } = useParams();
  const isEditing = !!id;
  const navigate = useNavigate();
  const { openSnackbar } = useContext(SnackbarContext);
  const [discount, setDiscount] = useState(blankDiscount);
  const [products, setProducts] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    document.title = isEditing ? 'Editar promoção - TudoPets.app' : 'Nova promoção - TudoPets.app';
  }, [isEditing]);

  useEffect(() => {
    const requests = [productsPathTo.list()];
    if (isEditing) {
      requests.push(discountsPathTo.get(id));
    }

    Promise.all(requests)
      .then(([productsResult, discountResult]) => {
        setProducts(productsResult.data);

        if (discountResult) {
          const loadedDiscount = discountResult.data;
          setDiscount({
            ...loadedDiscount,
            starts_at: toMomentDateTime(loadedDiscount.starts_at),
            ends_at: toMomentDateTime(loadedDiscount.ends_at),
          });
          setSelectedItems(itemsByProductId(loadedDiscount.discount_items));
        }
      })
      .finally(() => setLoading(false));
  }, [id, isEditing]);

  const selectedProducts = useMemo(
    () => products.filter((product) => selectedItems[product.id]?.selected),
    [products, selectedItems]
  );

  const handleDiscountChange = (event) => {
    const { name, value } = event.target;
    setDiscount({ ...discount, [name]: value });
  };

  const handleProductToggle = (product, checked) => {
    setSelectedItems({
      ...selectedItems,
      [product.id]: {
        ...(selectedItems[product.id] || {}),
        product_id: product.id,
        selected: checked,
        sale_price: selectedItems[product.id]?.sale_price || '',
      },
    });
  };

  const handleSalePriceChange = (productId, salePrice) => {
    setSelectedItems({
      ...selectedItems,
      [productId]: {
        ...(selectedItems[productId] || {}),
        product_id: productId,
        selected: true,
        sale_price: salePrice,
      },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedProducts.length === 0) {
      openSnackbar('Adicione pelo menos um produto à promoção.', 'error');
      return;
    }

    const invalidProduct = selectedProducts.find((product) => {
      const salePrice = Number(selectedItems[product.id]?.sale_price);
      return !salePrice || salePrice >= Number(product.price);
    });

    if (invalidProduct) {
      openSnackbar(`Informe um preço promocional menor que ${currency(invalidProduct.price)} para ${invalidProduct.title}.`, 'error');
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: discount.name,
        starts_at: toApiDateTime(discount.starts_at),
        ends_at: toApiDateTime(discount.ends_at),
      };

      const result = isEditing
        ? await discountsPathTo.update(id, payload)
        : await discountsPathTo.save(payload);

      const savedDiscount = result.data;
      await syncDiscountItems(savedDiscount);

      openSnackbar('Promoção salva com sucesso!', 'success');
      navigate('/provider-dashboard?p=promocoes');
    } catch (error) {
      openSnackbar(error?.response?.data?.errors?.[0] || 'Não foi possível salvar a promoção.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const syncDiscountItems = async (savedDiscount) => {
    const existingItems = savedDiscount.discount_items || [];
    const selected = Object.values(selectedItems).filter((item) => item.selected);
    const selectedProductIds = selected.map((item) => Number(item.product_id));
    const itemsToCreate = [];

    await Promise.all(
      existingItems.map((item) => {
        const selectedItem = selectedItems[item.product_id];
        if (!selectedItem?.selected) {
          return discountsPathTo.deleteItem(item.id);
        }

        return discountsPathTo.updateItem(item.id, {
          product_id: item.product_id,
          sale_price: selectedItem.sale_price,
        });
      })
    );

    selected.forEach((item) => {
      const alreadyExists = existingItems.some((existingItem) => existingItem.product_id === Number(item.product_id));
      if (!alreadyExists && selectedProductIds.includes(Number(item.product_id))) {
        itemsToCreate.push({
          product_id: item.product_id,
          sale_price: item.sale_price,
        });
      }
    });

    if (itemsToCreate.length > 0) {
      await discountsPathTo.addItems(savedDiscount.id, itemsToCreate);
    }
  };

  if (loading) {
    return <Typography>Carregando promoção...</Typography>;
  }

  return (
    <FormBox>
      <Box display="flex" flexDirection="column" gap={2}>
        <PageHeader
          title={isEditing ? 'Editar promoção' : 'Nova promoção'}
          onBack={() => navigate('/provider-dashboard?p=promocoes')}
        />

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="name"
              label="Nome da promoção"
              value={discount.name}
              onChange={handleDiscountChange}
              fullWidth
              required
            />

            <MobileDateTimePicker
              label="Início"
              value={discount.starts_at}
              onChange={(value) => setDiscount({ ...discount, starts_at: value })}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              closeOnSelect={false}
              sx={{ width: '100%', bgcolor: '#F5F8FA', borderRadius: '24px' }}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
                actionBar: {
                  actions: ['cancel', 'accept'],
                },
              }}
            />

            <MobileDateTimePicker
              label="Fim"
              value={discount.ends_at}
              onChange={(value) => setDiscount({ ...discount, ends_at: value })}
              format="DD/MM/YYYY HH:mm"
              ampm={false}
              closeOnSelect={false}
              sx={{ width: '100%', bgcolor: '#F5F8FA', borderRadius: '24px' }}
              slotProps={{
                textField: {
                  required: true,
                  fullWidth: true,
                },
                actionBar: {
                  actions: ['cancel', 'accept'],
                },
              }}
            />

            <Box>
              <Typography variant="body2" mb={1}>
                <b>Produtos da promoção</b>
              </Typography>
              <Stack spacing={1}>
                {products.map((product) => {
                  const selectedItem = selectedItems[product.id] || {};
                  const checked = selectedItem.selected === true;

                  return (
                    <Box key={product.id} sx={{ border: 1, borderColor: 'divider', borderRadius: '12px', p: 1.5 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={(event) => handleProductToggle(product, event.target.checked)}
                          />
                        }
                        label={`${product.title} (${currency(product.price)})`}
                      />

                      {checked && (
                        <Box mt={1}>
                          <CurrencyField
                            value={selectedItem.sale_price}
                            onChange={(salePrice) => handleSalePriceChange(product.id, salePrice)}
                          />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Box>

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" type="button" onClick={() => navigate('/provider-dashboard?p=promocoes')}>
                Cancelar
              </Button>
              <Button variant="contained" type="submit" disabled={saving}>
                {saving ? 'Salvando...' : 'Salvar promoção'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </FormBox>
  );
};

const itemsByProductId = (items) => {
  return (items || []).reduce((acc, item) => {
    acc[item.product_id] = {
      id: item.id,
      product_id: item.product_id,
      selected: true,
      sale_price: item.sale_price,
    };
    return acc;
  }, {});
};

export default ProviderDiscountForm;
